// Shared live-tracking hook. Encapsulates the GPS watcher, device-orientation
// listener, iOS permission gate, GPS-course fallback, and the compass-
// availability toast so both MapPage and LocationDetail (mini-map) can plug
// in with identical behaviour and correct unmount cleanup.
//
// Logic extracted verbatim from MapPage.jsx — no behavioural changes.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const POS_CACHE_KEY = "td_last_known_pos";

export function readCachedPos() {
  try {
    const raw = sessionStorage.getItem(POS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.position)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedPos(payload) {
  try { sessionStorage.setItem(POS_CACHE_KEY, JSON.stringify(payload)); } catch { /* quota or private mode — silent */ }
}

export function useLiveTracking() {
  const [userPos, setUserPos] = useState(null);
  const [heading, setHeading] = useState(null);
  const [tracking, setTracking] = useState(false);
  // Mobile-first "follow lock" — true when the map camera should
  // auto-pan with each GPS update. Any manual drag/touch on the map
  // flips this OFF (see FollowController) so the walker can free-roam
  // without the camera snapping back. The Locate Me button re-engages.
  const [isFollowing, setIsFollowing] = useState(false);
  const watchIdRef = useRef(null);
  const orientHandlerRef = useRef(null);
  const orientFiredRef = useRef(false);
  const compassHintTimerRef = useRef(null);
  const trackingRef = useRef(false);

  const startTracking = ({ onFirstFix, onError } = {}) => {
    if (trackingRef.current) return;
    trackingRef.current = true;
    let firstFix = true;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const payload = {
          position: [pos.coords.latitude, pos.coords.longitude],
          accuracy: pos.coords.accuracy || 60,
        };
        writeCachedPos(payload);
        setUserPos(payload);
        if (typeof pos.coords.heading === "number" && !Number.isNaN(pos.coords.heading) && pos.coords.speed > 0.5) {
          setHeading((prev) => (prev == null ? pos.coords.heading : prev));
        }
        if (firstFix) {
          firstFix = false;
          onFirstFix && onFirstFix(payload);
        }
      },
      (err) => { onError && onError(err); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5_000 },
    );
    watchIdRef.current = watchId;

    const attachOrientation = () => {
      const handler = (ev) => {
        let h = null;
        if (typeof ev.webkitCompassHeading === "number") {
          h = ev.webkitCompassHeading;
        } else if (ev.absolute && typeof ev.alpha === "number") {
          h = (360 - ev.alpha) % 360;
        }
        if (h != null && !Number.isNaN(h)) {
          orientFiredRef.current = true;
          setHeading(h);
        }
      };
      window.addEventListener("deviceorientationabsolute", handler, true);
      window.addEventListener("deviceorientation", handler, true);
      orientHandlerRef.current = handler;
    };

    const DOE = typeof window !== "undefined" ? window.DeviceOrientationEvent : null;
    if (DOE && typeof DOE.requestPermission === "function") {
      DOE.requestPermission()
        .then((state) => { if (state === "granted") attachOrientation(); })
        .catch(() => { /* denied — silently fall back to GPS course */ });
    } else if (typeof window !== "undefined" && "DeviceOrientationEvent" in window) {
      attachOrientation();
    }

    orientFiredRef.current = false;
    compassHintTimerRef.current = setTimeout(() => {
      let dismissed = false;
      try { dismissed = localStorage.getItem("td_compass_hint_dismissed") === "1"; } catch { /* ignore */ }
      if (orientFiredRef.current || dismissed) return;
      toast("Compass unavailable", {
        description: "Your device didn't share heading data — the beam will still track your position but won't rotate with the way you're facing.",
        duration: 8000,
        action: {
          label: "Got it",
          onClick: () => {
            try { localStorage.setItem("td_compass_hint_dismissed", "1"); } catch { /* ignore */ }
          },
        },
      });
    }, 3500);

    setTracking(true);
  };

  const stopTracking = () => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (orientHandlerRef.current) {
      window.removeEventListener("deviceorientationabsolute", orientHandlerRef.current, true);
      window.removeEventListener("deviceorientation", orientHandlerRef.current, true);
      orientHandlerRef.current = null;
    }
    if (compassHintTimerRef.current) {
      clearTimeout(compassHintTimerRef.current);
      compassHintTimerRef.current = null;
    }
    trackingRef.current = false;
    setTracking(false);
    setHeading(null);
    setIsFollowing(false);
  };

  // Cleanup on unmount — critical so we don't leak a background GPS watcher
  // or an orientation listener when the walker navigates away (e.g. closes
  // the trail-detail view).
  useEffect(() => () => { stopTracking(); }, []);

  return { userPos, setUserPos, heading, tracking, startTracking, stopTracking, isFollowing, setIsFollowing };
}
